import React from "react"
import clsx from "clsx"
import {
	Grid,
	Typography,
	LinearProgress,
	Card,
	Box
} from "@material-ui/core"

import { MeetingAnalysis } from "@/services/MeetingAnalyzerService"

import useMeetingAnalyzerStyles from "@/pages/Admin/MeetingAnalyzer/Detail/styles"
import useStyles from "@/pages/Admin/MeetingAnalyzer/Detail/ScoreCard/styles"
import {
	getScoreLabel,
	getScoreQualityColor,
	getScoreQualityLabel,
	meetingTopicDefinitions,
	scoreLabelToColor,
	scoreLabelToDisplayText
} from "@/utils/meetingAnalyzer"

type ScoreCardProps = {
	analysis: MeetingAnalysis
}

const ScoreCard: React.FC<ScoreCardProps> = (props) => {
	const { analysis } = props

	const meetingAnalyzerClasses = useMeetingAnalyzerStyles()
	const classes = useStyles()

	const overallScore = analysis.output?.generalScore ?? 0
	const overallScoreLabel = getScoreLabel(overallScore)
	const scoreColor = scoreLabelToColor[overallScoreLabel]

	return (
		<Card className={meetingAnalyzerClasses.card}>
			<Grid container spacing={4} alignItems="center">
				<Grid item xs={3}>
					<Grid container direction="column" spacing={2} className={classes.scoreColumn}>
						<Grid item>
							<Grid container direction="column" spacing={4} className={classes.scoreColumn}>
								<Grid item>
									<Typography className={classes.sectionLabel}>
										SCORE GERAL
									</Typography>
								</Grid>
								<Grid item>
									<Typography className={meetingAnalyzerClasses.scoreGeneralNumber}>
										{overallScore}
									</Typography>
								</Grid>
								<Grid item>
									<Typography className={classes.scoreOutOf}>
										de 100
									</Typography>
								</Grid>
							</Grid>
						</Grid>
						<Grid item>
							<Box className={classes.scoreBadgeWithDot} style={{ backgroundColor: scoreColor.badgeBackground }}>
								<Box className={classes.scoreBadgeDot} style={{ backgroundColor: scoreColor.dotColor }} />
								<Grid item>
									<Typography style={{ color: scoreColor.textColor, fontSize: "0.75rem", fontWeight: 600 }}>
										{overallScore} · {scoreLabelToDisplayText[overallScoreLabel]}
									</Typography>
								</Grid>
							</Box>
						</Grid>
					</Grid>
				</Grid>
				<Grid item xs={9}>
					<Grid container direction="column" spacing={2}>
						<Grid item>
							<Typography className={meetingAnalyzerClasses.cardTitle}>
								Visão por tópico
							</Typography>
						</Grid>
						<Grid item>
							<Grid container spacing={2}>
								{meetingTopicDefinitions.map(topic => {
									const topicScore = analysis.output?.topicScores[topic.key] ?? 0
									const topicColor = getScoreQualityColor(topicScore)

									return (
										<Grid item xs={4} key={topic.key}>
											<Box className={classes.topicSubCard}>
												<Grid item xs={12}>
													<Grid container justifyContent="space-between" alignItems="flex-start" wrap="nowrap">
														<Grid item>
															<Typography className={clsx(meetingAnalyzerClasses.topicLabel, classes.topicLabelFixedWidth)}>
																{topic.label}
															</Typography>
														</Grid>
														<Grid item>
															<Typography className={classes.topicWeightPill}>
																Peso {topic.weightInPercentage}%
															</Typography>
														</Grid>
													</Grid>
												</Grid>
												<Grid item xs={12}>
													<Grid container direction="column">
														<Grid container alignItems="baseline" spacing={1}>
															<Grid item>
																<Typography className={classes.topicScoreLarge} style={{ color: topicColor }}>
																	{topicScore}
																</Typography>
															</Grid>
															<Grid item>
																<Typography className={classes.topicScoreOutOf}>
																	/ 100
																</Typography>
															</Grid>
														</Grid>
													</Grid>
													<Grid item>
														<LinearProgress
															variant="determinate"
															value={topicScore}
															color="primary"
															classes={{ root: classes.topicProgressTrack, barColorPrimary: classes.topicProgressBar }}
														/>
													</Grid>
													<Grid item>
														<Typography className={classes.topicQualityLabel}>
															{getScoreQualityLabel(topicScore)}
														</Typography>
													</Grid>
												</Grid>
											</Box>
										</Grid>
									)
								})}
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Card>
	)
}

export default ScoreCard
